import { useBanners } from '@databases/banner/useBanners';
import { setView as setViewInState } from '@databases/database-state/state/view';
import { isStandalone } from '@databases/modes';
import { defaultStoreUtils } from '@databases/redux';
import {
  type DatabaseView,
  DEFAULT_DATABASE_VIEW_SETTINGS,
  ViewId,
} from '@databases/types/database/view';
import { isBrokenDatabase } from '@databases/utils/database/utils';
import {
  applyViewChange,
  getDefaultView,
  type ViewChangeType,
  ViewChangeTypes,
} from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';

import {
  initialLayoutSettings,
  type LayoutSettingsInterface,
  ViewLoader,
} from '../components/ViewLoader';
import { ViewPanelSettingType, type NavigationOptions } from '../types/types';

const DEFAULT_VIEW: DatabaseView = {
  id: '',
  name: '',
  settings: DEFAULT_DATABASE_VIEW_SETTINGS,
};

export interface ViewContextInterface {
  view: DatabaseView;
  unsavedView: DatabaseView | null;
  resolvedView: DatabaseView;
  setViewChange: (delta: ViewChangeType | ViewChangeType[]) => void;
  getViewChangeList: () => ViewChangeType[];
  resetViewChangeList: (viewId: string) => void;
  setViewId: (viewId: string) => void;
  activePanelSetting: ViewPanelSettingType | null;
  setActivePanelSetting: (
    setting: ViewPanelSettingType | null,
    options?: NavigationOptions
  ) => void;
  previousPanelSetting: ViewPanelSettingType;
  clearUnsavedView: () => void;
}

export const ViewContext = createContext<ViewContextInterface>({
  view: DEFAULT_VIEW,
  unsavedView: null,
  resolvedView: DEFAULT_VIEW,
  setViewChange: () => {},
  getViewChangeList: () => [],
  resetViewChangeList: () => {},
  setViewId: () => {},
  activePanelSetting: null,
  setActivePanelSetting: () => {},
  previousPanelSetting: ViewPanelSettingType.OVERVIEW,
  clearUnsavedView: () => {},
});

export interface ViewProviderProps {
  children: React.ReactNode;
  urlSavedViewId: string | null;
  initialUnsavedViewId: string | null;
  contentId: string;
}

export const ViewProvider = ({
  children,
  urlSavedViewId,
  initialUnsavedViewId,
  contentId,
}: ViewProviderProps) => {
  const databaseDefaultViewId = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdOptional(state, {
      contentId: contentId,
    });

    if (!database || isBrokenDatabase(database)) {
      return null;
    }

    const defaultView = getDefaultView(database);

    return defaultView ? defaultView.id : null;
  });

  const [viewChangeLists, setViewChangeLists] = useState<
    Record<string, ViewChangeType[]>
  >({});
  const [activePanelSetting, setActivePanelSettingState] =
    useState<ViewPanelSettingType | null>(null);
  const [previousPanelSetting, setPreviousPanelSetting] = useState<ViewPanelSettingType>(
    ViewPanelSettingType.OVERVIEW
  );

  const setActivePanelSetting = useCallback(
    (setting: ViewPanelSettingType | null, options?: NavigationOptions) => {
      // Set navigation context based on explicit 'from' option, otherwise default to Overview
      if (setting !== null && options?.from) {
        setPreviousPanelSetting(options.from);
      } else {
        setPreviousPanelSetting(ViewPanelSettingType.OVERVIEW);
      }

      setActivePanelSettingState(setting);
    },
    []
  );

  const [unsavedViewId, setUnsavedViewId] = useState<string | null>(initialUnsavedViewId);
  const [viewId, setViewId] = useState<string | null>(
    urlSavedViewId ?? databaseDefaultViewId
  );
  const [resolvedView, setResolvedView] = useState<DatabaseView>(DEFAULT_VIEW);

  const [view, setView] = useState<DatabaseView>(DEFAULT_VIEW);
  const [unsavedView, setUnsavedView] = useState<DatabaseView | null>(null);
  const [layoutSettings, setLayoutSettings] = useState<
    Record<string, LayoutSettingsInterface>
  >({});

  const lastViewedContentId = useRef(contentId);

  const { addBanner } = useBanners();

  useEffect(() => {
    if (initialUnsavedViewId) {
      if (urlSavedViewId === null || urlSavedViewId === ViewId.UNSAVED) {
        setViewId(ViewId.UNSAVED);
      }
      setUnsavedViewId(initialUnsavedViewId);
    }
  }, [urlSavedViewId, initialUnsavedViewId]);

  useEffect(() => {
    if (contentId !== lastViewedContentId.current) {
      setViewChangeLists({});
      setViewId(urlSavedViewId ?? databaseDefaultViewId);
      lastViewedContentId.current = contentId;
    }
  }, [contentId, lastViewedContentId, urlSavedViewId, databaseDefaultViewId]);

  const processViewChange = useCallback(
    (baseView: DatabaseView, delta: ViewChangeType) => {
      // if it's a ViewChangeTypes.LAYOUT_TYPE change
      // we need to maintain other settings for this view
      // so our base view need to get his previous state back (maintained in layoutSettings)

      const currentViewId = viewId !== null ? viewId : databaseDefaultViewId;
      if (delta.type === ViewChangeTypes.LAYOUT_TYPE && currentViewId) {
        baseView = {
          ...view,
          settings: {
            ...view.settings,
            layout: layoutSettings[currentViewId][delta.value],
          },
        };
      }

      return applyViewChange(baseView, delta);
    },
    [databaseDefaultViewId, layoutSettings, view, viewId]
  );

  const setViewChange = useCallback(
    (delta: ViewChangeType | ViewChangeType[]) => {
      if (!Array.isArray(delta)) {
        delta = [delta];
      }

      let newView = view;
      delta.forEach((change) => {
        newView = processViewChange(newView, change);
      });

      setLayoutSettings((prev) => {
        return {
          ...prev,
          [newView.id]: {
            ...prev[newView.id],
            [newView.settings.layout.type]: newView.settings.layout,
          },
        };
      });
      setView(newView);

      const targetViewId =
        viewId ?? (view.id === DEFAULT_VIEW.id ? databaseDefaultViewId : view.id);
      if (targetViewId) {
        setViewChangeLists((prevViewChangeLists) => {
          return {
            ...prevViewChangeLists,
            [targetViewId]: [...(prevViewChangeLists[targetViewId] ?? []), ...delta],
          };
        });
      }

      const nonSavedViewChangeTypes: ViewChangeType['type'][] = [
        ViewChangeTypes.VIEW_SEARCH,
      ];

      // These changes are not persisted with the view when saving it, so only trigger the
      // banner if some of the changes in the delta are *not* excluded types
      if (delta.some((d) => !nonSavedViewChangeTypes.includes(d.type))) {
        addBanner('unsaved-view-changes');
      }
    },
    [addBanner, databaseDefaultViewId, processViewChange, view, viewId]
  );

  const getView = useCallback(() => {
    const layoutSettingsForView = layoutSettings[view.id];
    const layoutForType = layoutSettingsForView
      ? layoutSettingsForView[view.settings.layout.type]
      : null;
    const newViewState = {
      ...view,
      settings: {
        ...view.settings,
        layout: layoutForType ?? initialLayoutSettings[view.settings.layout.type],
      },
    };
    setViewInState(newViewState);

    return newViewState;
  }, [layoutSettings, view]);

  const addViewIdToURL = useCallback((newViewId: string) => {
    if (isStandalone()) {
      const parentWindow = window.parent;
      const urlParams = new URLSearchParams(parentWindow.location.search);
      urlParams.set('savedViewId', newViewId);
      parentWindow.history.replaceState(
        {},
        '',
        `${parentWindow.location.pathname}?${urlParams}`
      );
    }
  }, []);

  const resetViewSearch = useCallback(() => {
    if (viewId) {
      setViewChangeLists((prevViewChangeLists) => {
        return {
          ...prevViewChangeLists,
          [viewId]: [
            ...(prevViewChangeLists[viewId]?.filter(
              (change) => change.type !== ViewChangeTypes.VIEW_SEARCH
            ) ?? []),
          ],
        };
      });
    }
  }, [viewId]);

  const onSetViewId = useCallback(
    (newViewId: string) => {
      resetViewSearch();
      setViewId(newViewId);
      addViewIdToURL(newViewId);
    },
    [addViewIdToURL, resetViewSearch]
  );

  const clearUnsavedView = useCallback(() => {
    setUnsavedView(null);
  }, []);

  const resetViewChangeList = useCallback((viewId: string) => {
    setViewChangeLists((prevViewChangeLists) => {
      return {
        ...prevViewChangeLists,
        [viewId]: [],
      };
    });
  }, []);

  const currentViewChangeList = useMemo(() => {
    const targetViewId =
      viewId ?? (view.id === DEFAULT_VIEW.id ? databaseDefaultViewId : view.id);
    return targetViewId ? viewChangeLists[targetViewId] ?? [] : [];
  }, [databaseDefaultViewId, view.id, viewChangeLists, viewId]);

  return (
    <ViewContext.Provider
      value={{
        view: getView(),
        unsavedView,
        setViewChange,
        getViewChangeList: () => currentViewChangeList,
        resetViewChangeList,
        activePanelSetting,
        setActivePanelSetting,
        previousPanelSetting,
        setViewId: onSetViewId,
        resolvedView,
        clearUnsavedView,
      }}
    >
      <ViewLoader
        contentId={contentId}
        setView={setView}
        viewId={viewId}
        urlSavedViewId={urlSavedViewId}
        unsavedViewId={unsavedViewId}
        unsavedView={unsavedView}
        viewChangeList={currentViewChangeList}
        setUnsavedView={setUnsavedView}
        setResolvedView={setResolvedView}
        setLayoutSettings={setLayoutSettings}
      />
      {children}
    </ViewContext.Provider>
  );
};
